import { createPortal } from 'react-dom';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { useDroppable } from '@dnd-kit/core';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '../../crm-theme';
import { DropAction } from '../../drag/crm-drag-data';
import { dropBarSx, dropZoneSx } from './crm-drop-actions.styles';
import { useProfilePermissions } from '@hooks/use-profile-permissions.hook';

interface ZoneProps {
    action: DropAction;
    label: string;
    icon: JSX.Element;
    color: string;
    visible: boolean;
}

const Zone = ({ action, label, icon, color, visible }: ZoneProps) => {
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const { setNodeRef, isOver } = useDroppable({
        id: `drop-action-${action}`,
        data: { type: 'action', action },
    });

    return (
        <Box ref={setNodeRef} sx={dropZoneSx(cc, color, visible, isOver)}>
            {icon}
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'inherit', textTransform: 'uppercase' }}>
                {label}
            </Typography>
        </Box>
    );
};

interface Props {
    visible: boolean;
    /// Sem outro quadro ativo, não há para onde mover.
    canMove: boolean;
}

/// Zonas do rodapé durante o arraste: soltar ali encerra o lead ou o leva a outro quadro.
/// Sempre montadas e no body: o dnd-kit mede os alvos no início do arraste.
export const CrmDropActions = ({ visible, canMove }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { canCloseLeads } = useProfilePermissions();
    // Sem nenhuma zona para o perfil, a barra nem aparece.
    const shown = visible && (canCloseLeads || canMove);

    return createPortal(
        <Box sx={dropBarSx(shown)}>
            {canCloseLeads && (
                <Zone
                    action="lost"
                    label={t('crmKanban.soltarPerdido')}
                    icon={<CloseIcon sx={{ fontSize: 18 }} />}
                    color={cc.eventDanger}
                    visible={shown}
                />
            )}
            {canCloseLeads && (
                <Zone
                    action="won"
                    label={t('crmKanban.soltarGanho')}
                    icon={<CheckIcon sx={{ fontSize: 18 }} />}
                    color={cc.eventSuccess}
                    visible={shown}
                />
            )}
            {canMove && (
                <Zone
                    action="move"
                    label={t('crmKanban.soltarMover')}
                    icon={<DriveFileMoveIcon sx={{ fontSize: 18 }} />}
                    color={cc.accent}
                    visible={shown}
                />
            )}
        </Box>,
        document.body
    );
};
